import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import {
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router-dom";

/**
 * Slot for the active product workspace breadcrumb — populated from
 * `ProductWorkspacePage` so the trail reads "Onboardings > Returns" instead
 * of the static literal "Workspace".
 */
export const productWorkspaceBreadcrumb = breadcrumbSlot();

type AnalyticsProductScope = "all" | "returns" | "order-editing";

function getAnalyticsUrl(
  productScope: AnalyticsProductScope,
  search: string,
  section?: string,
): string {
  const searchParams = new URLSearchParams(search);
  if (productScope === "all") {
    searchParams.delete("product");
  } else {
    searchParams.set("product", productScope);
  }
  if (productScope !== "returns") {
    searchParams.delete("section");
  } else if (section === "overview") {
    searchParams.delete("section");
  } else if (section != null) {
    searchParams.set("section", section);
  }

  const nextSearch = searchParams.toString();
  return `/onboardings/analytics${nextSearch ? `?${nextSearch}` : ""}`;
}

function LegacyAnalyticsRedirect() {
  const { search } = useLocation();
  const params = useParams();
  const slice = params.slice ?? "overview";
  let productScope: AnalyticsProductScope;
  switch (slice) {
    case "overview": {
      productScope = "all";
      break;
    }
    case "returns": {
      productScope = "returns";
      break;
    }
    case "order-editing":
    case "oms": {
      productScope = "order-editing";
      break;
    }
    default:
      productScope = "all";
  }
  return (
    <Navigate replace to={getAnalyticsUrl(productScope, search, params.tab)} />
  );
}

function ProductAnalyticsRedirect() {
  const { search } = useLocation();
  const { product } = useParams();
  const productScope: AnalyticsProductScope =
    product === "returns" || product === "order-editing" ? product : "all";
  return <Navigate replace to={getAnalyticsUrl(productScope, search)} />;
}

/**
 * Legacy `/onboardings/merchants[/:tab]` list URLs redirect into
 * `/onboardings/:product/merchants`.
 */
function LegacyMerchantsListRedirect() {
  const { tab } = useParams();
  const { search } = useLocation();
  const product = tab == null || tab === "" || tab === "all" ? "all" : tab;
  return <Navigate replace to={`/onboardings/${product}/merchants${search}`} />;
}

function OnboardingV2UrlRedirect() {
  const location = useLocation();
  const search = location.search;
  return <Navigate replace to={`/onboardings/all/merchants${search}`} />;
}

export const onboardingsRoutes: RouteObject[] = [
  {
    handle: { breadcrumb: "Onboardings" },
    async lazy() {
      const { Page } = await import("../app/page");
      return { element: <Page hidePadding={() => true} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="all/merchants" /> },

      // Back-compat redirects for bookmarks + older links.
      { path: "v2", element: <OnboardingV2UrlRedirect /> },
      { path: "v2/*", element: <OnboardingV2UrlRedirect /> },
      {
        path: "merchants",
        children: [
          {
            index: true,
            element: <Navigate replace to="/onboardings/all/merchants" />,
          },
          {
            path: "view/:teamId",
            handle: { breadcrumb: "Details" },
            async lazy() {
              const { V2MerchantDetailPage } =
                await import("./v2/V2MerchantDetailPage");
              return { Component: V2MerchantDetailPage };
            },
          },
          { path: ":tab", element: <LegacyMerchantsListRedirect /> },
        ],
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        async lazy() {
          const { AnalyticsPage } = await import("./analytics/AnalyticsPage");
          return { Component: AnalyticsPage };
        },
      },
      { path: "analytics/:slice", element: <LegacyAnalyticsRedirect /> },
      { path: "analytics/:slice/:tab", element: <LegacyAnalyticsRedirect /> },

      // Cross-product onboarding ops surface. Lives outside the per-product
      // workspace because the controls inside (HubSpot sync health, feature
      // flags, env inspection) apply to all onboarding products at once.
      {
        path: "settings",
        handle: { breadcrumb: "Settings" },
        children: [
          { index: true, element: <Navigate replace to="sync" /> },
          {
            path: ":section",
            async lazy() {
              const { SettingsPage } = await import("./settings/SettingsPage");
              return { Component: SettingsPage };
            },
          },
        ],
      },

      // New per-product workspace tree.
      {
        path: ":product",
        handle: { breadcrumb: productWorkspaceBreadcrumb },
        children: [
          { index: true, element: <Navigate replace to="merchants" /> },
          { path: "analytics", element: <ProductAnalyticsRedirect /> },
          {
            path: ":tab",
            async lazy() {
              const { ProductWorkspacePage } =
                await import("./workspace/ProductWorkspacePage");
              return { Component: ProductWorkspacePage };
            },
          },
        ],
      },
    ],
  },
];
